"use client";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons } from "@assets/assets";
import Navigation from "@/HomeFeatures/Navigation";
import React, { useEffect, useRef } from "react";
// PERF: GSAP + ScrollTrigger are loaded lazily via ensureGsap() so they stay out
// of the initial route bundle and registerPlugin runs after mount instead of at
// module evaluation.
import type GsapNS from "gsap";
import { ensureGsap } from "@/lib/EnsureGsap";
import { useScrollToCareerForm } from "@/lib/ScrollUtils";

const svgProps = {
  viewBox: "0 0 56 56",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 3.2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  className: "kc-svg h-9 w-9",
  "aria-hidden": true,
};

// Intern: a sprout that pops its leaves
const SproutIcon = () => (
  <svg {...svgProps}>
    <path d="M28 50V26" />
    <path className="kc-lf1" d="M28 34C17 34 11 28 11 18c11 0 17 5 17 16z" />
    <path className="kc-lf2" d="M28 27c0-10 6-16 17-16 0 10-6 16-17 16z" />
    <path d="M17 50h22" />
  </svg>
);

// Developer: </> brackets spread apart
const CodeIcon = () => (
  <svg {...svgProps}>
    <path className="kc-l" d="M20 18 8 28l12 10" />
    <path className="kc-r" d="M36 18l12 10-12 10" />
    <path className="kc-s" d="M32 12 24 44" />
  </svg>
);

// Senior: a bulb that lights up
const BulbIcon = () => (
  <svg {...svgProps}>
    <path
      className="kc-bulb"
      fill="currentColor"
      fillOpacity={0}
      d="M21 38c-4-3-7-7-7-12a14 14 0 0 1 28 0c0 5-3 9-7 12v3H21z"
    />
    <path d="M23 46h10M25 51h6" />
    <path className="kc-ray" d="M28 3v4M9 10l3 3M47 10l-3 3" />
  </svg>
);

// Team Lead: people move together
const TeamIcon = () => (
  <svg {...svgProps}>
    <g className="kc-pl">
      <circle cx="11" cy="24" r="4.5" />
      <path d="M3 42q0-8 8-8t8 8" />
    </g>
    <g className="kc-pr">
      <circle cx="45" cy="24" r="4.5" />
      <path d="M37 42q0-8 8-8t8 8" />
    </g>
    <g className="kc-pc">
      <circle cx="28" cy="19" r="6" />
      <path d="M17 48q0-11 11-11t11 11" />
    </g>
  </svg>
);

// Architect: a rocket that lifts off
const RocketIcon = () => (
  <svg {...svgProps}>
    <g className="kc-rocket">
      <path d="M28 5c8 6 11 15 9 26H19c-2-11 1-20 9-26z" />
      <circle cx="28" cy="19" r="3" />
      <path d="M19 31l-6 8 8-2" />
      <path d="M37 31l6 8-8-2" />
      <path className="kc-flame" d="M24 38v5M28 38v9M32 38v5" />
    </g>
  </svg>
);

type Step = {
  title: string;
  tagline: string;
  Icon: () => React.ReactElement;
  heightClass: string;
};

const STEPS: Step[] = [
  {
    title: "Intern",
    tagline: "Start curious, learn fast",
    Icon: SproutIcon,
    heightClass: "h-[110px] lg:h-[130px]",
  },
  {
    title: "Developer",
    tagline: "Build real products",
    Icon: CodeIcon,
    heightClass: "h-[135px] lg:h-[170px]",
  },
  {
    title: "Senior Engineer",
    tagline: "Own features end to end",
    Icon: BulbIcon,
    heightClass: "h-[160px] lg:h-[210px]",
  },
  {
    title: "Team Lead",
    tagline: "Guide people and projects",
    Icon: TeamIcon,
    heightClass: "h-[185px] lg:h-[250px]",
  },
  {
    title: "Architect",
    tagline: "Shape the big picture",
    Icon: RocketIcon,
    heightClass: "h-[210px] lg:h-[290px]",
  },
];

// Only transform / opacity / stroke-dashoffset are animated.
const KC_CSS = `
.kc-svg *{transform-box:fill-box;transform-origin:center}
.kc-svg .kc-l,.kc-svg .kc-r,.kc-svg .kc-s,.kc-svg .kc-pl,.kc-svg .kc-pr,
.kc-svg .kc-pc,.kc-svg .kc-rocket{transition:transform .45s cubic-bezier(.34,1.56,.64,1)}
.kc-svg .kc-lf1{transform-origin:100% 100%}
.kc-svg .kc-lf2{transform-origin:0% 100%}
.kc-svg .kc-ray{opacity:0;transition:opacity .3s}
.kc-svg .kc-bulb{transition:fill-opacity .4s}

/* Developer </> */
.kc-card:is(:hover,:focus-visible) .kc-l{transform:translateX(-4px)}
.kc-card:is(:hover,:focus-visible) .kc-r{transform:translateX(4px)}
.kc-card:is(:hover,:focus-visible) .kc-s{transform:rotate(14deg) scale(1.1)}

/* Intern sprout */
.kc-card:is(:hover,:focus-visible) .kc-lf1{animation:kc-pop .5s ease-out}
.kc-card:is(:hover,:focus-visible) .kc-lf2{animation:kc-pop .5s ease-out .12s backwards}

/* Senior bulb */
.kc-card:is(:hover,:focus-visible) .kc-ray{opacity:1}
.kc-card:is(:hover,:focus-visible) .kc-bulb{fill-opacity:.3}

/* Team lead */
.kc-card:is(:hover,:focus-visible) .kc-pl{transform:translateX(5px)}
.kc-card:is(:hover,:focus-visible) .kc-pr{transform:translateX(-5px)}
.kc-card:is(:hover,:focus-visible) .kc-pc{transform:translateY(-2px) scale(1.08)}

/* Architect rocket */
.kc-card:is(:hover,:focus-visible) .kc-rocket{transform:translateY(-5px) rotate(4deg)}
.kc-card:is(:hover,:focus-visible) .kc-flame{animation:kc-blink .25s linear infinite}

/* Floating icon bubbles */
.kc-bubble{animation:kc-float 3.2s ease-in-out infinite}


@keyframes kc-pop{0%{transform:scale(0)}60%{transform:scale(1.5)}100%{transform:scale(1)}}
@keyframes kc-blink{0%,100%{opacity:1}50%{opacity:.15}}
@keyframes kc-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}

@media (prefers-reduced-motion: reduce){
  .kc-svg *{animation:none !important;transition:none !important}
  .kc-bubble{animation:none !important}
}
`;

export default function CareerHero() {
  const stairsRef = useRef<HTMLDivElement | null>(null);
  const pageRootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) return;

    const container = stairsRef.current;
    if (!container) return;

    const steps = Array.from(
      container.querySelectorAll<HTMLElement>(".stair-item"),
    );
    if (steps.length === 0) return;

    // Accessibility: no intro for users who prefer reduced motion.
    // Steps are `lg:invisible` by default, so reveal them and stop here.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      steps.forEach((el) => {
        el.style.visibility = "visible";
      });
      return;
    }

    let cancelled = false;
    let heroMaster: ReturnType<(typeof GsapNS)["timeline"]> | null = null;
    let stairMaster: ReturnType<(typeof GsapNS)["timeline"]> | null = null;
    let cleanupST: (() => void) | null = null;

    ensureGsap().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;
      cleanupST = () => ScrollTrigger.getAll().forEach((t) => t.kill());

      const root = pageRootRef.current;
      const heroElements = root
        ? Array.from(root.querySelectorAll<HTMLElement>("[data-hero]"))
        : [];

      // LCP PROTECTION: the hero heading/copy render at full opacity in the
      // initial HTML (this is the LCP candidate on mobile). Never set their
      // opacity to 0/0.01 here — that re-hides already-painted content and
      // pushes Chrome's LCP timing out to whenever this fade-back finishes,
      // which is what was happening before. Only animate a transform.
      if (heroElements.length) {
        gsap.set(heroElements, { y: 40 });
        heroElements.forEach((el) =>
          el.classList.remove("opacity-0", "transform-y-[100px]"),
        );

        heroMaster = gsap.timeline();
        heroMaster.to(heroElements, {
          y: 0,
          ease: "power2.out",
          duration: 0.8,
          stagger: 0.1,
        });
      }

      // Staircase rises up from the bottom edge, one step after another.
      gsap.set(steps, { yPercent: 105, visibility: "visible" });
      stairMaster = gsap.timeline();
      stairMaster.to(
        steps,
        {
          yPercent: 0,
          ease: "power3.out",
          duration: 1,
          stagger: 0.12,
          onComplete: () => {
            gsap.set(steps, { clearProps: "transform" });
          },
        },
        0.4,
      );
    });

    return () => {
      cancelled = true;
      if (heroMaster) heroMaster.kill();
      if (stairMaster) stairMaster.kill();
      cleanupST?.();
    };
  }, []);

  const handleSmoothScroll = useScrollToCareerForm();

  return (
    <section className="relative h-[max-content] flex flex-col max-w-[100vw] overflow-hidden">
      <style>{KC_CSS}</style>
      <div className="relative z-10 w-full">
        <div
          ref={pageRootRef}
          className="w-full h-full bg-[#F3F7FE] flex flex-col pb-12 sm:min-h-screen sm:pb-0 relative isolate"
        >
          {/* Decorative background: soft glow + fading dot grid (behind content) */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-[-140px] -z-10 h-[460px] w-[760px] max-w-[140vw] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(34,91,212,0.16),transparent)]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 opacity-70 [background-image:radial-gradient(#C3D3F2_1px,transparent_1px)] [background-size:24px_24px] [mask-image:linear-gradient(to_bottom,black,transparent_65%)]"
          />
          <Navigation dark={false} />
          <div
            data-hero
            className="mx-auto mt-12 lg:mt-14 inline-flex w-fit items-center gap-2 rounded-full border border-[#DCE6FA] bg-white px-4 py-1.5 text-sm text-[#225BD4] shadow-[0_6px_20px_-12px_rgba(34,91,212,0.5)]"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            {"We're Hiring"}
          </div>
          <h1
            data-hero
            className="self-stretch pt-5 lg:pt-6 px-6 lg:px-0 mx-auto text-center max-w-[22ch] lg:max-w-[18ch] text-black text-4xl sm:text-5xl lg:text-[length:var(--service-banner-title)] font-normal clash-font capitalize leading-tight lg:leading-[100%]"
          >
            Be a Part of<span className="text-[#225BD4]"> Innovation</span>
          </h1>
          <p
            data-hero
            className="justify-start text-[#636364] px-6 lg:px-0 mx-auto text-base sm:text-lg lg:text-[length:var(--banner-para)] max-w-[540px] lg:max-w-[90%] text-center font-normal leading-[150%] tracking-tight py-4"
          >
            We are always on the hunt for talented professionals who bring
            genuine passion in what they do, excel in their expertise, and are
            committed to grow their knowledge and career with us.
          </p>
          <div
            data-hero
            className="flex flex-col items-center gap-4 px-6 lg:px-0"
          >
            <PrimaryBtn
              styleClass="mx-auto mt-6 lg:mt-[30px] min-h-[60px]"
              dark={false}
              text="See Current Opportunities"
              iconSrc={Icons?.chevron}
              onClick={() => {
                handleSmoothScroll();
              }}
            />
          </div>

          {/* Career-growth staircase, stuck to the bottom of the section */}
          <div
            ref={stairsRef}
            className="mt-auto hidden w-full px-4 pt-[50px] sm:block sm:px-8 lg:px-[6vw]"
          >
            <div className="mx-auto flex max-w-[1280px] items-end gap-2 sm:gap-3 lg:gap-4">
              {STEPS.map((step, i) => {
                const { Icon } = step;
                return (
                  // GSAP animates this wrapper; hover effects live on the inner
                  // elements so the two never overwrite each other's transform.
                  <div
                    key={step.title}
                    className="stair-item min-w-0 flex-1 lg:invisible"
                  >
                    <button
                      type="button"
                      onClick={() => handleSmoothScroll()}
                      aria-label={step.title}
                      className="kc-card group flex w-full flex-col items-center text-left outline-none focus-visible:ring-2 focus-visible:ring-[#225BD4] focus-visible:ring-offset-2"
                    >
                      <span className="flex w-full flex-col items-center transition-transform duration-300 ease-out lg:group-hover:-translate-y-2">
                        <span
                          className="kc-bubble mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-[#DCE6FA] bg-white text-[#225BD4] shadow-[0_10px_24px_-12px_rgba(34,91,212,0.5)] transition-colors duration-300 group-hover:bg-[#225BD4] group-hover:text-white lg:h-16 lg:w-16 [&_svg]:h-6 [&_svg]:w-6 lg:[&_svg]:h-9 lg:[&_svg]:w-9"
                          style={{ animationDelay: `${i * 0.4}s` }}
                        >
                          <Icon />
                        </span>

                        <span
                          className={`relative flex w-full flex-col overflow-hidden rounded-t-[20px] border border-b-0 border-[#DCE6FA] bg-gradient-to-b from-white to-[#E6EEFC] p-3 sm:p-4 lg:p-6 ${step.heightClass}`}
                        >
                          <span
                            aria-hidden="true"
                            className="absolute inset-0 bg-[#225BD4] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                          />
                          <span className="relative hidden text-xs font-medium sm:block text-[#225BD4]/70 transition-colors duration-300 group-hover:text-white/70 lg:text-sm">
                            {`0${i + 1}`}
                          </span>
                          <span className="relative mt-auto hidden sm:block">
                            <span className="clash-font block break-words text-[13px] font-normal leading-tight text-black transition-colors duration-300 group-hover:text-white sm:text-base lg:text-xl">
                              {step.title}
                            </span>
                            <span className="mt-1 hidden text-xs leading-[140%] text-[#636364] transition-colors duration-300 group-hover:text-white/80 sm:block lg:text-sm">
                              {step.tagline}
                            </span>
                          </span>
                        </span>
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
